import type { Course, CourseHole, CourseTee, Member, MemberPublic } from '@/lib/db/types';
import { courseHandicap as courseHcpFromIndex, groupRelativePlayingHandicap, playingHandicap } from '@/lib/games/handicap';

export const PLAYER_TINTS = ['#1e3a5f', '#8b6914', '#8a6d3d', '#15803d'] as const;

export type LiveEngineId =
  | 'live'
  | 'wolf'
  | 'dots'
  | 'putts'
  | 'events'
  | 'team'
  | 'rotation'
  | 'series';

export interface LivePlayer {
  id: string;
  name: string;
  avatar?: string | null;
  you: boolean;
  handicap: number;
  courseHcp: number;
  tint: string;
}

export interface EngineGameSnapshot {
  type: string;
  scoring?: string;
  amount?: number;
  opponentIds?: string[];
  board?: Array<{ id: string; name: string; you?: boolean; points?: number; total?: number }>;
  ledger?: unknown[];
  netOwed?: Record<string, number>;
  [key: string]: unknown;
}

export interface LiveEngineResultPayload {
  game: EngineGameSnapshot;
  post?: { course?: string; guests?: unknown[] };
  winnings?: number;
}

export function cleanName(name: string): string {
  return (name || 'Player').replace(/ "[^"]*"/, '');
}

export function buildLivePlayers(
  me: Member,
  members: MemberPublic[],
  partnerIds: string[] = members.slice(0, 3).map((m) => m.id),
): LivePlayer[] {
  const field = [
    me,
    ...partnerIds.map((id) => members.find((m) => m.id === id)).filter(Boolean) as MemberPublic[],
  ].slice(0, 4);

  return field.map((p, i) => {
    const you = p.id === me.id;
    const ch = courseHcpFromIndex(p.handicap ?? null);
    return {
      id: p.id,
      name: you ? 'You' : cleanName(p.name),
      avatar: p.avatar,
      you,
      handicap: p.handicap ?? 0,
      courseHcp: ch,
      tint: PLAYER_TINTS[i] || PLAYER_TINTS[0],
    };
  });
}

export function strokesOnForHole(courseHcp: number, holeStrokeIndex: number): number {
  const m = ((courseHcp % 18) + 18) % 18;
  return Math.floor(courseHcp / 18) + (holeStrokeIndex <= m ? 1 : 0);
}

export function netScore(
  gross: number,
  player: LivePlayer,
  hole: CourseHole,
  formatId = 'stroke',
  field?: Array<{ courseHcp: number }>,
): number {
  const ph = field && field.length >= 2
    ? groupRelativePlayingHandicap(field, player, formatId)
    : playingHandicap(player.courseHcp, formatId);
  return gross - strokesOnForHole(ph, hole.stroke_index);
}

export function balanceNetOwed(values: number[], multiplier: number): number[] {
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const rounded = values.map((p) => Math.round((p - mean) * multiplier));
  let drift = rounded.reduce((a, b) => a + b, 0);
  if (drift !== 0) {
    let idx = 0;
    for (let k = 1; k < rounded.length; k++) {
      if (rounded[k] > rounded[idx]) idx = k;
    }
    rounded[idx] -= drift;
  }
  return rounded;
}

export function netOwedFromPoints(
  players: LivePlayer[],
  points: number[],
  multiplier: number,
): Record<string, number> {
  const rounded = balanceNetOwed(points, multiplier);
  const netOwed: Record<string, number> = {};
  players.forEach((p, k) => {
    netOwed[p.id] = rounded[k];
  });
  return netOwed;
}

export function initGrossGrid(holes: CourseHole[], players = 4): number[][] {
  return holes.map((h) => Array(players).fill(h.par));
}

export function clampScore(v: number): number {
  return Math.max(1, Math.min(12, v));
}

export function randomGross(par: number): number {
  const r = Math.random();
  const d = r < 0.15 ? -1 : r < 0.55 ? 0 : r < 0.82 ? 1 : 2;
  return Math.max(1, par + d);
}

export function toParLabel(n: number): string {
  if (n === 0) return 'E';
  return n > 0 ? `+${n}` : `${n}`;
}

export function defaultEngineCourse(courses: Course[]): Course | undefined {
  return courses.find((c) => c.slug === 'arl-wb') || courses[0];
}

export function defaultEngineTee(tees: CourseTee[]): CourseTee | undefined {
  return tees.find((t) => t.name === 'White') || tees[0];
}

export function sortedHoles(holes: CourseHole[]): CourseHole[] {
  return [...holes].sort((a, b) => a.hole_num - b.hole_num);
}
